// Fitness age (العمر اللياقي, namat#56): computed at read time from the exam,
// never entered or stored. Pure; copied identically to mobile
// (src/lib/assessment/fitnessAge.ts) and the server
// (functions/assessment/fitnessAge.js); change all three together.
//
// Method: the NTNU (CERG) non-exercise fitness model.
//
// 1. Estimated VO2peak (mL/kg/min) from age, waist circumference, resting
//    heart rate and a physical-activity index (PA index):
//      men:   100.27 - 0.296*age - 0.369*waist - 0.155*RHR + 0.226*PA
//      women: 74.736 - 0.247*age - 0.259*waist - 0.114*RHR + 0.198*PA
//    Nes BM, Janszky I, Vatten LJ, Nilsen TIL, Aspenes ST, Wisløff U.
//    Estimating V̇O2peak from a nonexercise prediction model: the HUNT Study,
//    Norway. Med Sci Sports Exerc. 2011;43(11):2024-2030
//    (doi:10.1249/MSS.0b013e31821d3f6f). Coefficients as tabulated from that
//    paper in Peterman JE et al., J Am Heart Assoc. 2020;9:e015117, Table 1
//    (PMC7428991). NTNU's own Fitness Calculator (worldfitnesslevel.org, the
//    calculator behind ntnu.edu/cerg/vo2max) uses the same equations with the
//    waist coefficients rounded to 0.37 (men) and 0.258 (women).
//
// 2. PA index = frequency × duration × intensity, on the 0-45 scale the
//    coefficients were fitted on (Peterman 2020, Table 1 footnote: "HUNT
//    questionnaire (Scale, 0-45)"), with the answer weights of NTNU's Fitness
//    Calculator:
//      frequency: almost never / under once a week 0, once a week 1,
//                 2-3 times a week 2, almost every day 3
//      duration:  under 30 minutes 1, 30 minutes or more 1.5
//      intensity: easy, no hard breathing or sweating 1; somewhat hard,
//                 breathing hard and sweating 5; all out 10
//
// 3. Fitness age = the age at which the sex-specific population average
//    VO2peak equals the estimate (NTNU's Fitness Calculator, the inverse of
//    its normative average line):
//      men:   160.7 - 2.513 * VO2peak
//      women: 155.8 - 3.049 * VO2peak
//    The calculator reports it inside 20-75 ("under 20" / "over 75" beyond);
//    so does this module, with `bound` saying when it was clipped.

type Sex = 'male' | 'female';

export const ACTIVITY_FREQUENCIES = ['never', 'once', 'two_three', 'daily'] as const;
export const ACTIVITY_DURATIONS = ['under_30', 'over_30'] as const;
export const ACTIVITY_INTENSITIES = ['easy', 'moderate', 'hard'] as const;
export type ActivityFrequency = (typeof ACTIVITY_FREQUENCIES)[number];
export type ActivityDuration = (typeof ACTIVITY_DURATIONS)[number];
export type ActivityIntensity = (typeof ACTIVITY_INTENSITIES)[number];

export const ACTIVITY_WEIGHTS = {
  frequency: { never: 0, once: 1, two_three: 2, daily: 3 } as Record<ActivityFrequency, number>,
  duration: { under_30: 1, over_30: 1.5 } as Record<ActivityDuration, number>,
  intensity: { easy: 1, moderate: 5, hard: 10 } as Record<ActivityIntensity, number>,
};

export const VO2PEAK_MODEL: Record<Sex, { intercept: number; age: number; waist: number; restingHeartRate: number; activity: number }> = {
  male: { intercept: 100.27, age: 0.296, waist: 0.369, restingHeartRate: 0.155, activity: 0.226 },
  female: { intercept: 74.736, age: 0.247, waist: 0.259, restingHeartRate: 0.114, activity: 0.198 },
};

/** Fitness age = intercept - slope × VO2peak. */
export const FITNESS_AGE_LINE: Record<Sex, { intercept: number; slope: number }> = {
  male: { intercept: 160.7, slope: 2.513 },
  female: { intercept: 155.8, slope: 3.049 },
};

/** The calculator's reporting range. */
export const FITNESS_AGE_RANGE = { min: 20, max: 75 } as const;

const pick = <T extends string>(options: readonly T[], v: unknown): T | null =>
  typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : null;

/**
 * PA index from the three answers; null until they are answered. "Almost
 * never" gives 0 on its own, since duration and intensity multiply it.
 */
export function activityIndex(answers: { frequency?: unknown; duration?: unknown; intensity?: unknown }): number | null {
  const frequency = pick(ACTIVITY_FREQUENCIES, answers.frequency);
  if (frequency === null) return null;
  if (frequency === 'never') return 0;
  const duration = pick(ACTIVITY_DURATIONS, answers.duration);
  const intensity = pick(ACTIVITY_INTENSITIES, answers.intensity);
  if (duration === null || intensity === null) return null;
  const w = ACTIVITY_WEIGHTS;
  return w.frequency[frequency] * w.duration[duration] * w.intensity[intensity];
}

/** Estimated VO2peak, mL/kg/min (Nes 2011). */
export function estimateVo2Peak(input: {
  sex: Sex;
  ageYears: number;
  waistCm: number;
  restingHeartRate: number;
  activityIndex: number;
}): number {
  const m = VO2PEAK_MODEL[input.sex];
  return (
    m.intercept -
    m.age * input.ageYears -
    m.waist * input.waistCm -
    m.restingHeartRate * input.restingHeartRate +
    m.activity * input.activityIndex
  );
}

/** The age whose population average VO2peak equals `vo2Peak`, unrounded and unclipped. */
export function fitnessAgeFromVo2Peak(vo2Peak: number, sex: Sex): number {
  const line = FITNESS_AGE_LINE[sex];
  return line.intercept - line.slope * vo2Peak;
}

export interface FitnessAgeResult {
  vo2Peak: number;
  /** Whole years, clipped to FITNESS_AGE_RANGE as the calculator reports it. */
  fitnessAge: number;
  /** Whole years before clipping. */
  rawFitnessAge: number;
  /** 'under' when below 20, 'over' when above 75, otherwise null. */
  bound: 'under' | 'over' | null;
}

/** Fitness age from the model's inputs, or null when an input is missing. */
export function fitnessAge(input: {
  sex: Sex | null | undefined;
  ageYears: number | null | undefined;
  waistCm: number | null | undefined;
  restingHeartRate: number | null | undefined;
  frequency?: unknown;
  duration?: unknown;
  intensity?: unknown;
}): FitnessAgeResult | null {
  const { sex, ageYears, waistCm, restingHeartRate } = input;
  if (sex !== 'male' && sex !== 'female') return null;
  const positive = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
  if (!positive(ageYears) || !positive(waistCm) || !positive(restingHeartRate)) return null;
  const pa = activityIndex(input);
  if (pa === null) return null;
  const vo2Peak = estimateVo2Peak({ sex, ageYears, waistCm, restingHeartRate, activityIndex: pa });
  const rawFitnessAge = Math.round(fitnessAgeFromVo2Peak(vo2Peak, sex));
  const { min, max } = FITNESS_AGE_RANGE;
  const bound = rawFitnessAge < min ? 'under' : rawFitnessAge > max ? 'over' : null;
  return { vo2Peak, fitnessAge: Math.min(max, Math.max(min, rawFitnessAge)), rawFitnessAge, bound };
}
